import { writable } from "svelte/store";
import {
  ApiError,
  DefaultService,
  ParkitoAPI,
} from "../../apis/parkito/index.ts";
import { session } from "./session-parkito.ts";
import {
  type ApiCall as GeneralApiCall,
  err,
  Loading,
  ok,
  type Result,
} from "./result.ts";
import { setupTargetUrlStore } from "./api.ts";

const DEFAULT_PARKITO_PROD_BASE_URL: string = "https://parkito.parkando.cloud";
export const isLoading = writable<boolean>(false);
export const targetUrl = setupTargetUrlStore(DEFAULT_PARKITO_PROD_BASE_URL);

let numberOfOutstandingRequests = 0;
const api = new ParkitoAPI().default;

targetUrl.subscribe((baseUrl) => {
  if (import.meta.env.DEV) console.log(`Setting baseUrl: "${baseUrl}"`);
  api.httpRequest.config.BASE = baseUrl;
});

export async function withApi<T>(
  fn: (api: DefaultService) => Promise<T>,
): Promise<Result<T, ApiError>> {
  numberOfOutstandingRequests += 1;
  if (0 < numberOfOutstandingRequests) {
    isLoading.set(true);
  }

  const res: Result<T, ApiError> = await fn(api)
    .then((res: T) => {
      return ok<T, ApiError>(res);
    })
    .catch((error: ApiError) => {
      if (import.meta.env.DEV) console.log("api error", error);
      return err(error);
    });

  numberOfOutstandingRequests -= 1;

  if (numberOfOutstandingRequests < 1) {
    isLoading.set(false);
  }

  return res;
}

export type ApiCall<T> = GeneralApiCall<T, ApiError>;
export function fromApi<T>(
  fn: (api: DefaultService) => Promise<T>,
): ApiCall<T> {
  const apiCall: ApiCall<T> = writable(new Loading<T, ApiError>());

  const doCall = async () => {
    const res = await withApi(fn);
    apiCall.set(res);
  };

  doCall();

  return apiCall;
}

const standardHeaders = {
  "parkito-web": "unknown version",
};

session.subscribe((session) => {
  if (session) {
    api.httpRequest.config.HEADERS = {
      "X-Authorization": "Bearer " + session.jwt,
      ...standardHeaders,
    };
  } else {
    api.httpRequest.config.HEADERS = standardHeaders;
  }
});
